import { useSettingsStore } from '../../stores/settingsStore';

/**
 * Eye icon beside the wallet pill that toggles privacy ("streamer") mode.
 *
 * When on, every USD value across the app renders as `*******` so the operator
 * can stream / screenshot without leaking balances, P&L, or order sizes. The
 * masking itself lives in the shared formatters (tradeMetrics.ts) driven by a
 * module-level flag the settings store keeps in sync; this is just the switch.
 */

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className ?? 'w-4 h-4'}
      aria-hidden="true"
    >
      {/* Heroicons 24/solid — eye */}
      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
      <path
        fillRule="evenodd"
        d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function EyeSlashIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className ?? 'w-4 h-4'}
      aria-hidden="true"
    >
      {/* Heroicons 24/solid — eye-slash */}
      <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
      <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
      <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
    </svg>
  );
}

export function PrivacyToggle() {
  const privacyMode = useSettingsStore((s) => s.privacyMode);
  const togglePrivacyMode = useSettingsStore((s) => s.togglePrivacyMode);

  return (
    <button
      type="button"
      onClick={togglePrivacyMode}
      title={privacyMode ? 'Privacy mode on — USD values hidden' : 'Hide USD values (privacy mode)'}
      aria-label={privacyMode ? 'Disable privacy mode' : 'Enable privacy mode'}
      aria-pressed={privacyMode}
      className={`flex items-center justify-center rounded border p-1.5 transition-colors ${
        privacyMode
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-border bg-bg-secondary text-text-muted hover:text-text-secondary hover:border-accent/40'
      }`}
    >
      {privacyMode ? <EyeSlashIcon /> : <EyeIcon />}
    </button>
  );
}
